import { SlideRef } from '@/components/slides/Slide';
import { Z_INDEX_UI } from '@/constant';
import { Coordinate, PropsWithPreview, Size } from '@/types/slide';
import { DndContext, DragEndEvent, PointerSensor, useDraggable, useSensor, useSensors } from '@dnd-kit/core';
import { Box, BoxProps } from '@mui/material';
import { forwardRef, RefObject, useState } from 'react';

type ExtraBoundary = {
    right: number;
    bottom: number;
};

const DragDot = forwardRef<HTMLElement, BoxProps>(({ ...props }, ref) => {
    return (
        <Box
            sx={{
                width: 10,
                height: 10,
                border: 1.5,
                borderRadius: 0.5,
                borderColor: 'secondary.main',
                backgroundColor: 'white',
                touchAction: 'none',
                ...props.sx,
            }}
            {...props}
            ref={ref}
        />
    );
});

const DraggableTopLeftDragDot = () => {
    const { attributes, listeners, setNodeRef } = useDraggable({
        id: 'resize-handle-top-left',
    });

    return (
        <DragDot
            aria-label='resize-handle-top-left'
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            style={{
                cursor: 'nwse-resize',
                zIndex: Z_INDEX_UI,
                position: 'absolute',
                top: -6,
                left: -6,
                pointerEvents: 'auto',
            }}
        />
    );
};

const DraggableTopRightDragDot = () => {
    const { attributes, listeners, setNodeRef } = useDraggable({
        id: 'resize-handle-top-right',
    });

    return (
        <DragDot
            aria-label='resize-handle-top-right'
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            style={{
                cursor: 'nesw-resize',
                zIndex: Z_INDEX_UI,
                position: 'absolute',
                top: -6,
                right: -6,
                pointerEvents: 'auto',
            }}
        />
    );
};

const DraggableBottomLeftDragDot = () => {
    const { attributes, listeners, setNodeRef } = useDraggable({
        id: 'resize-handle-bottom-left',
    });

    return (
        <DragDot
            aria-label='resize-handle-bottom-left'
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            style={{
                cursor: 'nesw-resize',
                zIndex: Z_INDEX_UI,
                position: 'absolute',
                bottom: -6,
                left: -6,
                pointerEvents: 'auto',
            }}
        />
    );
};

const DraggableBottomRightDot = () => {
    const { attributes, listeners, setNodeRef } = useDraggable({
        id: 'resize-handle-bottom-right',
    });

    return (
        <DragDot
            aria-label='resize-handle-bottom-right'
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            style={{
                cursor: 'nwse-resize',
                zIndex: Z_INDEX_UI,
                position: 'absolute',
                bottom: -6,
                right: -6,
                pointerEvents: 'auto',
            }}
        />
    );
};

type DraggableBorderProps = {
    isSelected?: boolean;
    disabled?: boolean;
};
// Add this new component for draggable border
const DraggableBorder: React.FC<DraggableBorderProps> = ({ isSelected, disabled }) => {
    const { attributes, listeners, setNodeRef } = useDraggable({
        id: 'move-handle',
    });

    return (
        <Box
            id='move-handle'
            aria-label='move-handle'
            ref={setNodeRef}
            {...(!disabled && listeners)}
            {...(!disabled && attributes)}
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                ...(!disabled && {
                    cursor: 'move',
                }),
                touchAction: 'none',
                // Make sure it doesn't interfere with children
                pointerEvents: 'auto',
                // Make sure all border itself can be interacted with
                '& > div': {
                    pointerEvents: 'auto',
                },
                // Add hover style with dashed outline
                ...(!isSelected
                    ? {
                          '&:hover': {
                              outline: '1px solid',
                              outlineColor: (theme) => (theme.vars || theme).palette.grey[400],
                              outlineOffset: '1px',
                              outlineStyle: 'dashed',
                          },
                      }
                    : {}),
            }}
        >
            {/* Top border */}
            <Box sx={{ position: 'absolute', top: 0, left: 10, right: 10, height: 6, cursor: 'move' }} />
            {/* Left border */}
            <Box sx={{ position: 'absolute', top: 10, left: 0, bottom: 10, width: 6, cursor: 'move' }} />
            {/* Right border */}
            <Box sx={{ position: 'absolute', top: 10, right: 0, bottom: 10, width: 6, cursor: 'move' }} />
            {/* Bottom border */}
            <Box sx={{ position: 'absolute', bottom: 0, left: 10, right: 10, height: 6, cursor: 'move' }} />
        </Box>
    );
};

interface Props {
    minSize?: Size;
    maxSize?: Size;
    size: Size;
    pos: Coordinate;
    isSelected: boolean;
    children?: React.ReactNode;
    onResize?: (size: Size, pos: Coordinate) => void;
    onResizeComplete?: (size: Size, pos: Coordinate) => void;
    aspectRatio?: number;
    disabled?: boolean;
    resizable?: boolean;
    extraBoundary?: ExtraBoundary;
    slideRef: RefObject<SlideRef | null>;
}

export const ResizeBorder: React.FC<PropsWithPreview<Props>> = ({
    size,
    pos,
    isSelected,
    children,
    onResize,
    onResizeComplete,
    preview,
    aspectRatio,
    disabled = false,
    resizable = true,
    minSize,
    maxSize,
    extraBoundary = { right: 0, bottom: 0 },
    slideRef,
}) => {
    const [tSize, setTSize] = useState<Size>({ width: 0, height: 0 });
    const [tPos, setTPos] = useState<Coordinate>({ x: 0, y: 0 });

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 1,
            },
        }),
    );

    const handleDragStart = () => {
        setTSize(size);
        setTPos(pos);
    };

    const calculateNewSize = (event: DragEndEvent) => {
        if (!event.delta || !slideRef.current) {
            return { size: { width: tSize.width, height: tSize.height }, pos: { x: tPos.x, y: tPos.y } };
        }

        const { x, y } = event.delta;
        const canvasSize = slideRef.current.size();

        // Apply inverse scaling to maintain proper resize behavior
        const scaledX = x / slideRef.current.scale();
        const scaledY = y / slideRef.current.scale();

        // Default minimum size is 50px if minSize is not provided
        const minWidth = minSize?.width ?? 50;
        const minHeight = minSize?.height ?? 50;

        // Handle move operation
        if (event.active.id === 'move-handle') {
            if (!canvasSize) {
                return { size: { width: tSize.width, height: tSize.height }, pos: { x: tPos.x, y: tPos.y } };
            }

            const newX = Math.max(0, Math.min(canvasSize.width - tSize.width - extraBoundary.right, tPos.x + scaledX));
            const newY = Math.max(0, Math.min(canvasSize.height - tSize.height - extraBoundary.bottom, tPos.y + scaledY));

            // Check if we're hitting boundaries and prevent movement in that direction
            const updatedPos = {
                x: newX,
                y: newY,
            };

            return {
                size: { width: tSize.width, height: tSize.height },
                pos: updatedPos,
            };
        }
        // Handle different corners differently
        else if (event.active.id === 'resize-handle-top-left') {
            if (!canvasSize) {
                return { size: { width: tSize.width, height: tSize.height }, pos: { x: tPos.x, y: tPos.y } };
            }

            // Calculate new dimensions with min and max size constraints
            let newWidth = Math.max(minWidth, tSize.width - scaledX);
            let newHeight = Math.max(minHeight, tSize.height - scaledY);

            // Apply max size constraints if provided
            if (maxSize) {
                newWidth = Math.min(maxSize.width, newWidth);
                newHeight = Math.min(maxSize.height, newHeight);
            }

            // Apply aspect ratio if provided and not 0
            if (aspectRatio && aspectRatio !== 0) {
                // Determine which dimension is changing more
                const widthChange = newWidth - tSize.width;
                const heightChange = newHeight - tSize.height;

                if (widthChange >= heightChange * aspectRatio) {
                    // Width is the primary change, adjust height to match
                    newHeight = newWidth / aspectRatio;
                } else {
                    // Height is the primary change, adjust width to match
                    newWidth = newHeight * aspectRatio;
                }
            }

            // Calculate potential new positions
            let newX = tPos.x - (newWidth - tSize.width);
            let newY = tPos.y - (newHeight - tSize.height);

            // Apply boundary constraints
            let hitXBoundary = false;
            if (newX < 0) {
                newWidth = tSize.width + tPos.x;
                newX = 0;
                hitXBoundary = true;
                // Recalculate height if aspect ratio is enforced
                if (aspectRatio && aspectRatio !== 0) {
                    newHeight = newWidth / aspectRatio;
                }
            }

            // Only check Y boundary if X boundary wasn't hit
            if (newY < 0 && !hitXBoundary) {
                newHeight = tSize.height + tPos.y;
                newY = 0;
                // Recalculate width if aspect ratio is enforced
                if (aspectRatio && aspectRatio !== 0) {
                    newWidth = newHeight * aspectRatio;
                    // Recalculate X position based on new width
                    newX = tPos.x - (newWidth - tSize.width);
                }
            } else if (hitXBoundary) {
                // If X boundary was hit, recalculate Y position and height based on aspect ratio
                newY = tPos.y - (newHeight - tSize.height);
                if (aspectRatio && aspectRatio !== 0) {
                    newHeight = newWidth / aspectRatio;
                    // Adjust Y position based on new height while maintaining bottom position
                    newY = tPos.y - (newHeight - tSize.height);
                    // Ensure Y doesn't go below 0
                }
                if (newY < 0) {
                    newHeight = tSize.height + tPos.y;
                    newY = 0;
                    // Recalculate width if aspect ratio is enforced
                    if (aspectRatio && aspectRatio !== 0) {
                        newWidth = newHeight * aspectRatio;
                        // Recalculate X position based on new width
                        newX = tPos.x - (newWidth - tSize.width);
                    }
                }
            }

            return {
                size: { width: newWidth, height: newHeight },
                pos: { x: newX, y: newY },
            };
        } else if (event.active.id === 'resize-handle-bottom-left') {
            if (!canvasSize) {
                return { size: { width: tSize.width, height: tSize.height }, pos: { x: tPos.x, y: tPos.y } };
            }

            // Calculate new dimensions with min and max size constraints
            let newWidth = Math.max(minWidth, tSize.width - scaledX);
            let newHeight = Math.max(minHeight, tSize.height + scaledY);

            // Apply max size constraints if provided
            if (maxSize) {
                newWidth = Math.min(maxSize.width, newWidth);
                newHeight = Math.min(maxSize.height, newHeight);
            }

            // Apply aspect ratio if provided and not 0
            if (aspectRatio && aspectRatio !== 0) {
                // Determine which dimension is changing more
                const widthChange = newWidth - tSize.width;
                const heightChange = newHeight - tSize.height;

                if (widthChange >= heightChange * aspectRatio) {
                    // Width is the primary change, adjust height to match
                    newHeight = newWidth / aspectRatio;
                } else {
                    // Height is the primary change, adjust width to match
                    newWidth = newHeight * aspectRatio;
                }
            }

            // Calculate potential new position for x
            let newX = tPos.x - (newWidth - tSize.width);

            // Apply boundary constraints
            if (newX < 0) {
                newWidth = tSize.width + tPos.x;
                newX = 0;
                // Recalculate height if aspect ratio is enforced
                if (aspectRatio && aspectRatio !== 0) {
                    newHeight = newWidth / aspectRatio;
                }
            }

            // Ensure bottom edge doesn't exceed canvas height
            if (tPos.y + newHeight + extraBoundary.bottom > canvasSize.height) {
                newHeight = canvasSize.height - extraBoundary.bottom - tPos.y;
                // Recalculate width if aspect ratio is enforced
                if (aspectRatio && aspectRatio !== 0) {
                    newWidth = newHeight * aspectRatio;
                    // Recalculate X position based on new width
                    newX = tPos.x - (newWidth - tSize.width);
                }
            }

            return {
                size: { width: newWidth, height: newHeight },
                pos: { x: newX, y: tPos.y },
            };
        } else if (event.active.id === 'resize-handle-top-right') {
            if (!canvasSize) {
                return { size: { width: tSize.width, height: tSize.height }, pos: { x: tPos.x, y: tPos.y } };
            }

            // Calculate new dimensions with min and max size constraints
            let newWidth = Math.max(minWidth, tSize.width + scaledX);
            let newHeight = Math.max(minHeight, tSize.height - scaledY);

            // Apply max size constraints if provided
            if (maxSize) {
                newWidth = Math.min(maxSize.width, newWidth);
                newHeight = Math.min(maxSize.height, newHeight);
            }

            // Apply aspect ratio if provided and not 0
            if (aspectRatio && aspectRatio !== 0) {
                // Determine which dimension is changing more
                const widthChange = newWidth - tSize.width;
                const heightChange = newHeight - tSize.height;

                if (widthChange >= heightChange * aspectRatio) {
                    // Width is the primary change, adjust height to match
                    newHeight = newWidth / aspectRatio;
                } else {
                    // Height is the primary change, adjust width to match
                    newWidth = newHeight * aspectRatio;
                }
            }

            // Calculate potential new position for y
            let newY = tPos.y - (newHeight - tSize.height);

            // Apply boundary constraints
            if (tPos.x + newWidth + extraBoundary.right > canvasSize.width) {
                newWidth = canvasSize.width - extraBoundary.right - tPos.x;
                // Recalculate height if aspect ratio is enforced
                if (aspectRatio && aspectRatio !== 0) {
                    newHeight = newWidth / aspectRatio;
                    // Recalculate Y position based on new height
                    newY = tPos.y - (newHeight - tSize.height);
                }
            }

            if (newY < 0) {
                newHeight = tSize.height + tPos.y;
                newY = 0;
                // Recalculate width if aspect ratio is enforced
                if (aspectRatio && aspectRatio !== 0) {
                    newWidth = newHeight * aspectRatio;
                }
            }

            return {
                size: { width: newWidth, height: newHeight },
                pos: { x: tPos.x, y: newY },
            };
        } else {
            // Bottom-right corner
            if (!canvasSize) {
                return { size: { width: tSize.width, height: tSize.height }, pos: { x: tPos.x, y: tPos.y } };
            }

            // Calculate new dimensions with min and max size constraints
            let newWidth = Math.max(minWidth, tSize.width + scaledX);
            let newHeight = Math.max(minHeight, tSize.height + scaledY);

            // Apply max size constraints if provided
            if (maxSize) {
                newWidth = Math.min(maxSize.width, newWidth);
                newHeight = Math.min(maxSize.height, newHeight);
            }

            // Apply aspect ratio if provided and not 0
            if (aspectRatio && aspectRatio !== 0) {
                // Determine which dimension is changing more
                const widthChange = newWidth - tSize.width;
                const heightChange = newHeight - tSize.height;

                if (widthChange >= heightChange * aspectRatio) {
                    // Width is the primary change, adjust height to match
                    newHeight = newWidth / aspectRatio;
                } else {
                    // Height is the primary change, adjust width to match
                    newWidth = newHeight * aspectRatio;
                }
            }

            // Apply boundary constraints
            if (tPos.x + newWidth + extraBoundary.right > canvasSize.width) {
                newWidth = canvasSize.width - extraBoundary.right - tPos.x;
                // Recalculate height if aspect ratio is enforced
                if (aspectRatio && aspectRatio !== 0) {
                    newHeight = newWidth / aspectRatio;
                }
            }

            if (tPos.y + newHeight + extraBoundary.bottom > canvasSize.height) {
                newHeight = canvasSize.height - extraBoundary.bottom - tPos.y;
                // Recalculate width if aspect ratio is enforced
                if (aspectRatio && aspectRatio !== 0) {
                    newWidth = newHeight * aspectRatio;
                }
            }

            return {
                size: { width: newWidth, height: newHeight },
                pos: { x: tPos.x, y: tPos.y },
            };
        }
    };

    const handleDragMove = (event: DragEndEvent) => {
        if (onResize && event.delta) {
            const { size, pos } = calculateNewSize(event);
            onResize(size, pos);
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        if (onResizeComplete && event.delta) {
            const { size, pos } = calculateNewSize(event);
            onResizeComplete(size, pos);
        }
    };

    return (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragMove={handleDragMove} onDragEnd={handleDragEnd}>
            <Box
                sx={{
                    pointerEvents: 'none',
                    zIndex: Z_INDEX_UI,
                    width: size.width + extraBoundary.right,
                    height: size.height + extraBoundary.bottom,
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    ...(!preview
                        ? {
                              ...(isSelected
                                  ? {
                                        outline: '2px solid',
                                        outlineColor: (theme) => (theme.vars || theme).palette.secondary.main,
                                        outlineOffset: '0px',
                                    }
                                  : {
                                        '&:hover': {
                                            outline: '1px solid',
                                            outlineColor: (theme) => (theme.vars || theme).palette.grey[400],
                                            outlineOffset: '1px',
                                            outlineStyle: 'dashed',
                                        },
                                    }),
                          }
                        : {}),
                }}
            >
                {isSelected && resizable && <DraggableTopLeftDragDot />}
                {isSelected && resizable && <DraggableTopRightDragDot />}
                {isSelected && resizable && <DraggableBottomLeftDragDot />}
                {isSelected && resizable && <DraggableBottomRightDot />}
            </Box>
            {!preview && <DraggableBorder isSelected={isSelected} disabled={disabled} />}
            <Box
                sx={{
                    pointerEvents: 'none',
                    width: size.width + extraBoundary.right,
                    height: size.height + extraBoundary.bottom,
                    position: 'relative',
                    whiteSpace: 'pre-wrap',
                    '&:hover': !isSelected
                        ? {
                              outline: '1px solid',
                              outlineColor: (theme) => (theme.vars || theme).palette.grey[400],
                              outlineOffset: '1px',
                              outlineStyle: 'dashed',
                          }
                        : undefined,
                }}
            >
                {children}
            </Box>
        </DndContext>
    );
};
